import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/pricing", label: "Pricing" },
    { to: "/#media", label: "Media" },
    { to: "/reviews", label: "Reviews" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
];

const ABOUT_LINKS = [
    { to: "/founder", label: "Founder" },
    { to: "/company", label: "Company" },
    { to: "/investors", label: "Investors" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [aboutOpen, setAboutOpen] = useState(false);
    const location = useLocation();
    const aboutActive = ABOUT_LINKS.some((l) => l.to === location.pathname);

    const linkCls = (isActive) =>
        `rounded-full px-3 py-2 text-[13px] font-semibold transition-colors ${
            isActive ? "bg-blue-600/10 text-blue-700" : "text-slate-600 hover:bg-blue-100/60 hover:text-blue-700"
        }`;

    return (
        <header
            className="fixed inset-x-0 top-0 z-50 border-b border-blue-100 bg-gradient-to-r from-white via-blue-50 to-white/95 shadow-[0_4px_30px_rgba(37,99,235,0.10)] backdrop-blur-xl"
            data-testid="main-navbar"
        >
            <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link to="/" data-testid="nav-logo-link" onClick={() => { setOpen(false); setAboutOpen(false); }}>
                    <Logo />
                </Link>

                <div className="hidden items-center gap-0.5 lg:flex">
                    {LINKS.slice(0, 4).map((l) =>
                        l.to.includes("#") ? (
                            <a
                                key={l.to}
                                href={l.to}
                                data-testid="nav-media-link"
                                className="rounded-full px-3 py-2 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-blue-100/60 hover:text-blue-700"
                            >
                                {l.label}
                            </a>
                        ) : (
                            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}-link`} className={linkCls}>
                                {l.label}
                            </NavLink>
                        )
                    )}

                    {/* About Us dropdown */}
                    <div className="relative">
                        <button
                            type="button"
                            data-testid="nav-about-button"
                            onClick={() => setAboutOpen(!aboutOpen)}
                            aria-expanded={aboutOpen}
                            className={`flex items-center gap-1 rounded-full px-3 py-2 text-[13px] font-semibold transition-colors ${
                                aboutActive || aboutOpen ? "bg-blue-600/10 text-blue-700" : "text-slate-600 hover:bg-blue-100/60 hover:text-blue-700"
                            }`}
                        >
                            About Us <ChevronDown className={`h-3.5 w-3.5 transition-transform ${aboutOpen ? "rotate-180" : ""}`} />
                        </button>
                        {aboutOpen && (
                            <div
                                className="absolute left-0 top-full mt-2 w-44 overflow-hidden rounded-2xl border border-blue-100 bg-white p-1.5 shadow-xl shadow-blue-500/10"
                                data-testid="nav-about-menu"
                            >
                                {ABOUT_LINKS.map((l) => (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        data-testid={`nav-${l.label.toLowerCase()}-link`}
                                        onClick={() => setAboutOpen(false)}
                                        className={({ isActive }) =>
                                            `block rounded-xl px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                                                isActive ? "bg-blue-600/10 text-blue-700" : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>

                    {LINKS.slice(4).map((l) => (
                        <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}-link`} className={linkCls}>
                            {l.label}
                        </NavLink>
                    ))}
                </div>

                <div className="flex items-center gap-2.5">
                    <LanguageSwitcher light />
                    <Link
                        to="/contact"
                        data-testid="nav-cta-button"
                        className="hidden items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition-transform hover:scale-[1.03] sm:flex"
                    >
                        Get a Demo <ArrowRight className="h-4 w-4" />
                    </Link>
                    <button
                        data-testid="nav-mobile-menu-button"
                        className="rounded-lg border border-blue-200 bg-white/70 p-2 text-slate-700 lg:hidden"
                        onClick={() => setOpen(!open)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </nav>

            {open && (
                <div className="border-t border-blue-100 bg-white/97 px-4 py-4 shadow-xl backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
                    <div className="flex flex-col gap-1">
                        {LINKS.slice(0, 4).map((l) =>
                            l.to.includes("#") ? (
                                <a
                                    key={l.to}
                                    href={l.to}
                                    data-testid="nav-mobile-media-link"
                                    onClick={() => setOpen(false)}
                                    className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                                >
                                    {l.label}
                                </a>
                            ) : (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
                                    onClick={() => setOpen(false)}
                                    className={`rounded-lg px-4 py-2.5 text-sm font-semibold ${
                                        location.pathname === l.to ? "bg-blue-600/10 text-blue-700" : "text-slate-600 hover:bg-blue-50"
                                    }`}
                                >
                                    {l.label}
                                </Link>
                            )
                        )}

                        {/* About Us group (mobile) */}
                        <p className="mt-2 px-4 text-[10px] font-bold uppercase tracking-wider text-slate-400" data-testid="nav-mobile-about-label">
                            About Us
                        </p>
                        {ABOUT_LINKS.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
                                onClick={() => setOpen(false)}
                                className={`rounded-lg px-4 py-2.5 pl-7 text-sm font-semibold ${
                                    location.pathname === l.to ? "bg-blue-600/10 text-blue-700" : "text-slate-600 hover:bg-blue-50"
                                }`}
                            >
                                {l.label}
                            </Link>
                        ))}

                        {LINKS.slice(4).map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
                                onClick={() => setOpen(false)}
                                className={`rounded-lg px-4 py-2.5 text-sm font-semibold ${
                                    location.pathname === l.to ? "bg-blue-600/10 text-blue-700" : "text-slate-600 hover:bg-blue-50"
                                }`}
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="nav-mobile-cta-button"
                            onClick={() => setOpen(false)}
                            className="mt-2 flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-2.5 text-sm font-semibold text-white"
                        >
                            Get a Demo <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};
